import { getSettingSignUpBusinessTypes } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingSignUpBusinessType";
import {
  SignUpPill,
  SignUpPillContainer,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/SignUpPill";
import { useSaveIncompleteSignup } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useSaveIncompleteSignup";
import { useSignUpEventTracker } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { useSignUpQueryParams } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useSignUpQueryParams";
import { handleChangeWithoutErrorWhenEmpty } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_utils/handleChangeWithoutErrorWhenEmpty";
import { Step } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/Step";
import { trackSignupIfCustomerSignUpClick } from "@faire/web-api--source/events/signup/click/ifCustomerSignUp";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import consumerPath from "@faire/web-api--source/routes/www/welcome/r/consumer";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { TextArea } from "@faire/web/slate/Input/TextArea";
import { Column, Grid } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Field, Form, useFormikContext } from "formik";
import React from "react";
import styled from "styled-components";
import * as Yup from "yup";

export type BusinessTypeData = Pick<
  ISignUpRequest,
  "business_platforms" | "business_type"
> & {
  other_business_platform_description?: string;
};

type BusinessPlatformOption = keyof Omit<
  typeof ISignUpRequest.BusinessPlatform,
  "BUSINESS_PLATFORM_UNKNOWN"
>;

export const BusinessTypeForm = () => {
  const businessTypes = getSettingSignUpBusinessTypes().filter(
    (type) => type !== ISignUpRequest.BusinessPlatform.BUSINESS_PLATFORM_UNKNOWN
  ) as BusinessPlatformOption[];
  const businessTypeInfo = useBusinessTypeInfo();
  const {
    values,
    isValid,
    dirty,
    setValues,
    touched,
    errors,
    getFieldProps,
    setFieldTouched,
    handleChange,
    handleBlur,
  } = useFormikContext<BusinessTypeData>();
  const { queryParams } = useSignUpQueryParams();
  const { saveIncompleteSignupIfConsumer } = useSaveIncompleteSignup();

  const { strictLocalize } = useStrictLocalization();
  const { trackFieldValue } = useSignUpEventTracker(Step.BUSINESS_TYPE);
  const isOtherSelected = values.business_platforms.includes(
    ISignUpRequest.BusinessPlatform.OTHER_PLATFORM
  );
  const hasRequiredInitialValues =
    !!values.business_platforms.length && !!values.business_type;
  const isBrandInvoicingRetailer = !!queryParams.brandInvoice;

  const handleClick = (
    businessType: keyof typeof ISignUpRequest.BusinessPlatform
  ) => {
    const selectedValues = new Set(values.business_platforms);
    if (selectedValues.has(businessType)) {
      selectedValues.delete(businessType);
    } else {
      selectedValues.add(businessType);
    }
    // Go through business type list in order of priority and add the first match
    let business_type = undefined;
    for (const bt of businessTypes) {
      if (selectedValues.has(bt)) {
        business_type = bt;
        break;
      }
    }
    trackFieldValue(
      "business_platforms",
      Array.from(selectedValues).toString() ?? ""
    );
    const other_business_platform_description = selectedValues.has(
      ISignUpRequest.BusinessPlatform.OTHER_PLATFORM
    )
      ? values.other_business_platform_description
      : undefined;

    setValues(
      {
        ...values,
        business_platforms: Array.from(selectedValues),
        business_type,
        other_business_platform_description,
      },
      true
    );
  };

  return (
    <Form
      style={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container>
        {businessTypes.map((businessType) => (
          <SignUpPill
            showCheckbox
            key={businessType}
            title={businessTypeInfo[businessType].label}
            subtitle={businessTypeInfo[businessType].subtext}
            isSelected={values.business_platforms.includes(businessType)}
            onClick={() => handleClick(businessType)}
            dataTestId={businessType}
          />
        ))}
      </Container>
      {isOtherSelected ? (
        <>
          <Spacer height="4x" />
          <Field
            as={TextArea}
            label={strictLocalize({
              id: "NjHGX4",
              defaultMessage: "Describe where you sell your products",
              description: {
                text: "prompt for user to desribe their store type",
              },
            })}
            {...getFieldProps("other_business_platform_description")}
            error={
              touched.other_business_platform_description &&
              errors.other_business_platform_description
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChangeWithoutErrorWhenEmpty(
                e,
                "other_business_platform_description",
                setFieldTouched,
                handleChange
              )
            }
            onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => {
              trackFieldValue(
                "other_business_platform_description",
                values.other_business_platform_description ?? ""
              );
              handleBlur(e);
            }}
            maxLength={250}
            rows={4}
          />
        </>
      ) : null}
      <Column>
        <Spacer
          height={{
            mobileAndAbove: "2x",
            desktopAndAbove: "5x",
          }}
        />
        <Button
          disabled={!isValid || (!dirty && !hasRequiredInitialValues)}
          type="submit"
          data-test-id="next"
        >
          <StrictLocalMsg
            id="m853T8"
            defaultMessage="Next"
            description={{ text: "Button to visit the next page" }}
          />
        </Button>
        <Spacer
          height={{
            mobileAndAbove: "2x",
            desktopAndAbove: "3x",
          }}
        />
        {!isBrandInvoicingRetailer ? (
          <Button
            variant="tertiary"
            linkTo={consumerPath({ ...queryParams })}
            onClick={async () => {
              trackSignupIfCustomerSignUpClick();
              await saveIncompleteSignupIfConsumer();
            }}
          >
            <StrictLocalMsg
              id="65bDmc"
              defaultMessage="I'm just shopping for myself"
            />
          </Button>
        ) : null}
      </Column>
    </Form>
  );
};

export const useSchema = () => {
  const { strictLocalize } = useStrictLocalization();

  return Yup.object().shape({
    business_platforms: Yup.array()
      .of(Yup.string())
      .min(
        1,
        strictLocalize({
          id: "PPLP3R",
          defaultMessage: "Please select an option",
        })
      ),
    business_type: Yup.string().required(
      strictLocalize({
        id: "PPLP3R",
        defaultMessage: "Please select an option",
      })
    ),
    other_business_platform_description: Yup.string().when(
      "business_platforms",
      {
        is: (business_platforms: ISignUpRequest.BusinessPlatform[]) =>
          business_platforms.includes(
            ISignUpRequest.BusinessPlatform.OTHER_PLATFORM
          ),
        then: (schema) =>
          schema.required(
            strictLocalize({
              id: "0ajvN4",
              defaultMessage: "Please enter your store description",
            })
          ),
      }
    ),
  });
};

const useBusinessTypeInfo = (): Record<
  BusinessPlatformOption,
  { label: string; subtext: string | undefined }
> => {
  const { strictLocalize } = useStrictLocalization();

  return {
    [ISignUpRequest.BusinessPlatform.BRICK_AND_MORTAR]: {
      label: strictLocalize({
        id: "Zs2Prz",
        defaultMessage: "Brick and mortar store",
        description: {
          text: "A business type option for a physical store",
          img: "https://cdn.faire.com/fastly/51ea0b953364879c68f38f1d3fb0a22b3a43346b32a2fbe4416d8e69cfac8397.png",
        },
      }),
      subtext: strictLocalize({
        id: "MxLJdK",
        defaultMessage: "A permanent retail location",
        description: {
          text: "A subtext for the brick and mortar store option",
          img: "https://cdn.faire.com/fastly/51ea0b953364879c68f38f1d3fb0a22b3a43346b32a2fbe4416d8e69cfac8397.png",
        },
      }),
    },
    [ISignUpRequest.BusinessPlatform.ONLINE_ONLY]: {
      label: strictLocalize({
        id: "ccU6l/",
        defaultMessage: "Online",
      }),
      subtext: strictLocalize({
        id: "k4YhxK",
        defaultMessage: "My website or social channel",
        description: {
          text: "A subtext for the online option",
          img: "https://cdn.faire.com/fastly/51ea0b953364879c68f38f1d3fb0a22b3a43346b32a2fbe4416d8e69cfac8397.png",
        },
      }),
    },

    [ISignUpRequest.BusinessPlatform.POP_UP]: {
      label: strictLocalize({
        id: "+gB4W4",
        defaultMessage: "Pop-up shop",
        description: {
          text: "Indicating retailer business type is pop-up store",
        },
      }),
      subtext: strictLocalize({
        id: "CMXET9",
        defaultMessage: "A temporary retail location",
        description: {
          text: "A subtext for the pop-up option",
          img: "https://cdn.faire.com/fastly/51ea0b953364879c68f38f1d3fb0a22b3a43346b32a2fbe4416d8e69cfac8397.png",
        },
      }),
    },
    [ISignUpRequest.BusinessPlatform.OTHER_PLATFORM]: {
      label: strictLocalize({
        id: "PufzFw",
        defaultMessage: "Somewhere else",
      }),
      subtext: undefined,
    },
  };
};

const Container = styled(Grid)`
  grid-template-columns: repeat(1, 1fr);
  grid-gap: ${getSpacing(12)};

  ${SignUpPillContainer} {
    width: 100%;
    max-width: 100%;
  }
`;
